import { useEffect } from 'react';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom';
import { SupportChat } from './components/SupportChat';
import { ConsentProvider } from './consent/ConsentContext';
import { CookieBanner } from './consent/CookieBanner';
import { EarlyAccessPage } from './pages/EarlyAccessPage';
import { MarketingHome } from './pages/MarketingHome';
import { PricingPage } from './pages/PricingPage';
import { DocsLayout } from './pages/docs/DocsLayout';
import { BlogHome } from './pages/blog/BlogHome';
import { BlogArticle } from './pages/blog/BlogArticle';
import { GlossaryIndex, GlossaryTermPage } from './pages/glossary/GlossaryPages';
import { CookiePage } from './pages/legal/CookiePage';
import { PrivacyPage } from './pages/legal/PrivacyPage';
import { SubProcessorsPage } from './pages/legal/SubProcessorsPage';
import { TermsPage } from './pages/legal/TermsPage';
import { LEGAL_ROUTES } from './pages/legal/legal';

/**
 * `scripts/prerender-website.js` sets this flag before the app boots while it snapshots each route
 * into static HTML, so overlays that only make sense for a live visitor (cookie banner, chat
 * widget) stay out of the markup search and AI crawlers see.
 */
function isPrerendering(): boolean {
  return (window as unknown as { __REVVLY_PRERENDER__?: boolean }).__REVVLY_PRERENDER__ === true;
}

/** Scroll to the top of the page on route changes (skip in-page hash links). */
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return;
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

export function App() {
  return (
    <BrowserRouter>
      <ConsentProvider>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<MarketingHome />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/early-access" element={<EarlyAccessPage />} />
          <Route path="/docs/*" element={<DocsLayout />} />
          <Route path="/blog" element={<BlogHome />} />
          <Route path="/blog/:slug" element={<BlogArticle />} />
          <Route path="/glossary" element={<GlossaryIndex />} />
          <Route path="/glossary/:slug" element={<GlossaryTermPage />} />
          <Route path={LEGAL_ROUTES.terms} element={<TermsPage />} />
          <Route path={LEGAL_ROUTES.privacy} element={<PrivacyPage />} />
          <Route path={LEGAL_ROUTES.cookies} element={<CookiePage />} />
          <Route path={LEGAL_ROUTES.subprocessors} element={<SubProcessorsPage />} />
        </Routes>
        {/* Client-only chrome: never part of the static snapshot crawlers read. */}
        {!isPrerendering() && (
          <>
            <SupportChat />
            <CookieBanner />
          </>
        )}
      </ConsentProvider>
    </BrowserRouter>
  );
}
